import type { ApolloClient } from '@apollo/client';
import type { SxProps, Theme } from '@mui/material';
import { Box } from '@mui/material';
import {
  DataGridPremium,
  DataGridPremiumProps,
  GridCell,
  GridCellProps,
  GridColDef,
  GridFilterModel,
  type GridGroupNode,
  GridPaginationModel,
  GridRowId,
  GridSortModel,
  GridValidRowModel,
  isGroupingColumn,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { PageInfo } from '@shared/types/index';
import type { ComponentProps, ReactElement, FocusEvent as ReactFocusEvent } from 'react';
import { createElement, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isRowSerializingExport, useGridExportState } from 'src/hooks/useGridExport';
import { useRowEditSubscriptions } from 'src/providers/EditingStateProvider';
import GridCoreProvider, { useGridCore } from 'src/providers/GridCoreProvider';
import { useGridFocus } from 'src/providers/GridFocusProvider';
import { resolveColumnOrder } from 'src/utils/grid/column/resolveColumnOrder';
import {
  baseGridStyles,
  GRID_COLUMN_HEADER_HEIGHT,
  type GridSurface,
  gridRowHeight,
  gridSurfaceSx,
  MIN_ROW_HEIGHT,
  resolveGridSurface,
} from 'src/utils/grid/gridStyles';
import { GridLiveRegion, useGridResultAnnouncement } from './a11y';
import { withDateValueGetter } from './columns/dateColumnValue';
import { useGridColumnState } from './columns/useGridColumnState';
import { useGridGrouping } from './grouping';
import { GridPerformanceProfiler } from './performance';
import { pagedAriaRowCount } from './rows/ariaRowCount';
import NoRowsOverlay from './rows/NoRowsOverlay';
import { PagedGridRow, PagedRowIndexFrameProvider } from './rows/pagedRowIndex';
import type { GridRowsState, LocalGridRowsState, PagedGridRowsState } from './rows/useGridRows';
import {
  type DataSourceAllRowsResolver,
  type DataSourceChildRowsResolver,
  type PagedAllRowsResolver,
  type PagedGroupRowsResolver,
  type PagedRowGroupKeyGetter,
  useDataSourceSelection,
  useGridSelection,
} from './selection';
import { pagedRequestKey, pageInfoRequestKey } from './serverEnhanced/useServerEnhancedGridQuery';
import type { BaseGridStateWithHelpers } from './shared/baseGridTypes';
import { GridRowsReadinessProvider } from './shared/gridRowsReadiness';
import Toolbar, { ToolbarProps } from './toolbar/Toolbar';

/** Scalars a grid filter carries — the `PrismaFilter` value domain, plus the nullish a source row may hold. */
export type GridFilterValue = string | number | boolean | null | undefined;
export type GridFilterOption = { value: GridFilterValue; label: string };
type GridSx = SxProps<Theme>;

/** Plain-object `sx` layers merge by TOP-LEVEL KEY REPLACEMENT — a caller's `'& .MuiDataGrid-row'` replaces the base block outright. Use the array form for arrays and theme callbacks. */
const mergeGridSx = (...styles: Array<GridSx | undefined>): GridSx => {
  const present = styles.filter((style): style is GridSx => Boolean(style));
  const isPlainObject = (style: GridSx) => typeof style === 'object' && !Array.isArray(style);

  if (present.every(isPlainObject)) {
    return Object.assign({}, ...present) as GridSx;
  }

  return present.flatMap((style) => (Array.isArray(style) ? style : [style]));
};
type ColumnKeyType<Model extends GridValidRowModel = GridValidRowModel> = GridColDef<Model>['field'];
type ColumnWithoutField<Column> = Column extends unknown ? Omit<Column, 'field'> : never;

export type GridColValue<Model extends GridValidRowModel = GridValidRowModel> = ColumnWithoutField<
  GridColDef<Model>
> & {
  serverFilterValueTransform?: { from: (value: unknown) => unknown; to: (value: unknown) => unknown };
  getFilterSelectOptions?:
    | string[]
    | GridFilterOption[]
    | ((client: ApolloClient<object>) => Promise<string[] | GridFilterOption[]>);
  customFilterType?: 'scalar_string' | 'read_only' | 'string_array' | 'money' | 'locked';
  lookupFilterLabel?(client: ApolloClient<object>, id: GridFilterValue): Promise<string | undefined>;
  popoutWidth?: number;
  /** CSS selector for the focusable element inside a custom edit cell (default 'input') */
  focusableElement?: string;
  /**
   * Column-level per-row rule, mirrored by `renderEditCell` (BlankCell vs input); it drives
   * disabled styling plus keyboard and click gating. NOT collapsible into MUI's grid-level
   * `isCellEditable`, which gates edit permission only.
   */
  isCellEditable?: (row: Model) => boolean;
  /** DISPLAY ONLY — this does not gate editing, `isCellEditable` does. Take the pair from `usePolicy().column(field)` so the two cannot disagree. */
  lockReason?: (row: Model) => string | null;
};
export type GridColObj<Model extends GridValidRowModel = GridValidRowModel> = Record<
  ColumnKeyType<Model>,
  GridColValue<Model>
>;

export type DisabledColumns = Record<string, boolean>;

type SharedDataGridProps = {
  /**
   * THE identity of this grid instance — required, never defaulted.
   *
   *   gridKey ──▶ row-edit bucket   EditingStateProvider.rowEditing / stopRowEditCallbacks
   *           ──▶ focus arbitration GridFocusProvider.focusedGrid
   *
   * Two grids on one page share ONE EditingStateProvider store (a transition
   * must wait on every grid), so the BUCKET inside it is what must differ.
   * A missing key collapsed two grids into one bucket: one grid's release
   * dropped the other's pending edits, and "Waiting for changes to save..."
   * never closed.
   *
   * NOT `storageKey` (localStorage column layout) and NOT `gridId` (the
   * `data-grid-id` DOM attribute e2e selects on). Those two keep their own
   * meanings; this one is identity.
   */
  gridKey: string;
  toolbar?: ToolbarProps;
  /**
   * Columns that are disabled from being shown
   */
  disabledColumns?: DisabledColumns;
  /** Columns that start hidden but stay in the gear — `useGridColumnState`'s `hiddenByDefault`. */
  hiddenByDefault?: readonly string[];

  columnOrderToSync?: string[];

  variant?: 'list' | 'grid';

  height?: number | string;

  gridId?: string;

  /**
   * Whether the user can edit cells in the grid.
   * When false, clipboard paste is also disabled.
   */
  canEdit?: boolean;

  /**
   * Determine whether a row should refuse the processRowUpdate changes
   */
  shouldPreventRowProcess?: (
    rowId: GridRowId,
    oldRow: Record<string, unknown>,
    newRow: Record<string, unknown>
  ) => boolean | Promise<boolean>;

  /**
   * LEGACY spelling of `surface` — prefer `surface`. Still passed by ~27 call sites and it also
   * drives `baseGridStyles`' own background.
   */
  noBorder?: boolean;

  surface?: GridSurface;

  /** mode="paged" — the server page window returned alongside the rows. */
  pageInfo?: PageInfo;

  /** mode="paged" — total rows; cached locally for the pages where the server omits it. */
  totalCount?: number | null;

  /** mode="paged" — label for the no-rows overlay. */
  noResultsLabel?: string;

  /** Resets the cursor window when a domain scope outside StandardPageInput changes. */
  paginationResetKey?: string | number | null;

  /** Internal row-query success signal used by deferred toolbar work. */
  rowsReady?: boolean;

  /** mode="paged" — forwarded to MUI unchanged; declared so it does not leak as a DOM attribute. */
  autosize?: boolean;

  /**
   * mode="local" — selecting a parent row selects all its children.
   * Requires treeData={true} and getTreeDataPath.
   */
  enableTreeSelection?: boolean;

  /**
   * mode="local" — INERT. Declared so the prop does not leak to MUI; nothing reads it.
   * Carried over unchanged from the deleted local grid.
   */
  isSelectableParent?: (row: GridValidRowModel) => boolean;

  /** Resolves every concrete row in a selected server-paged group. */
  resolvePagedGroupRows?: PagedGroupRowsResolver;

  /** Resolves every concrete row in the current filtered scope for header selection. */
  resolveAllPagedRows?: PagedAllRowsResolver;

  /** Returns the group key used to associate fully resolved rows with MUI group nodes. */
  getPagedRowGroupKey?: PagedRowGroupKeyGetter;

  /** Invalidates an in-flight paged group selection when its server scope changes. */
  pagedGroupSelectionScopeKey?: string;

  /** Checkbox selection, plus the accumulation of selected rows into the grid state. */
  enableSelection?: boolean;

  /** mode="dataSource" — a row whose selection means "every one of its server children". */
  isParentRow?: (row: GridValidRowModel) => boolean;

  /** mode="dataSource" — every child of a selected parent in the current scope, loaded or not. */
  resolveChildRows?: DataSourceChildRowsResolver;

  /** mode="dataSource" — every leaf in the current scope, for the header checkbox. */
  resolveAllRows?: DataSourceAllRowsResolver;

  /** mode="dataSource" — the parent a leaf belongs to, so select-all can be unticked parent by parent. */
  getParentRowId?: (row: GridValidRowModel) => GridRowId | undefined;

  /** mode="dataSource" — changing it drops the selection and ignores responses in flight. */
  selectionScopeKey?: string;

  /**
   * mode <-> enhancedGridState is enforced by `PagedDataGridProps` / `LocalDataGridProps` plus
   * a runtime guard in `DataGrid`, deliberately NOT a discriminated union: a union here makes
   * every `{...props}` spread at the plain call sites fail to resolve a member.
   */
  mode?: GridMode;

  enhancedGridState?: BaseGridStateWithHelpers | GridRowsState;

  sx?: GridSx;
};

export type GridMode = 'local' | 'paged' | 'dataSource';

const MODE_STATE_MISMATCH = (mode: GridMode) =>
  `DataGrid mode="${mode}" requires a useGridRows({ mode: '${mode}' }) state. ` +
  'A mismatched state fails plausibly rather than loudly — a paged grid handed a local ' +
  'state filters only the visible page and looks correct.';

const DATA_SOURCE_OWNER_CONFLICT =
  'A DataGrid `dataSource` owns its rows and pagination. It is only valid with mode="dataSource", and ' +
  'that mode takes no `rows`, `pageInfo`, `totalCount` or paged-selection resolvers — two owners would ' +
  'each overwrite the other.';

/** `apiRef` after `DataGrid` has resolved one. MUI v9 keeps `.current` nullable until mount. */
export type GridApiRef = NonNullable<DataGridPremiumProps['apiRef']>;

type MuiGridSlots = NonNullable<DataGridPremiumProps['slots']>;
type MuiToolbarSlot = NonNullable<MuiGridSlots['toolbar']>;
type AppGridSlots = Omit<MuiGridSlots, 'toolbar'> & {
  /** Existing app contract: callers may pass either a slot component or an already-built element. */
  toolbar?: MuiToolbarSlot | ReactElement | null;
};

export type PersistentDataGridProps<T extends GridValidRowModel = GridValidRowModel> = Omit<
  DataGridPremiumProps<T>,
  'apiRef' | 'slots' | 'sx'
> &
  SharedDataGridProps & { storageKey?: string; apiRef: GridApiRef; slots?: AppGridSlots };

export type DataGridProps<T extends GridValidRowModel = GridValidRowModel> = Omit<
  DataGridPremiumProps<T>,
  'columns' | 'slots' | 'sx'
> &
  SharedDataGridProps & {
    columns: GridColObj<T>;
    storageKey: string;
    appendToColumns?: Record<string, Partial<GridColValue<T>>>;
    onAfterFirstLoad?: (filterModel: GridFilterModel, sortModel: GridSortModel) => void;
    slots?: AppGridSlots;
  };

/**
 * Deliberately NOT generic: the mode bodies are where `T` settles to `any`, which is what lets
 * `DataGrid<T>` hand its props down without a cast.
 */
export type PagedDataGridProps<T extends GridValidRowModel = GridValidRowModel> = {
  mode: 'paged';
  enhancedGridState: PagedGridRowsState;
} & DataGridProps<T>;

/** `<DataGrid mode="local" />` props — same shape rule as above. */
export type LocalDataGridProps<T extends GridValidRowModel = GridValidRowModel> = {
  mode: 'local';
  enhancedGridState: LocalGridRowsState;
  /** Required in local mode — the filter runs over these in the browser. */
  rows: NonNullable<DataGridProps<T>['rows']>;
} & DataGridProps<T>;

/**
 * `<DataGrid mode="dataSource" />` — MUI's `dataSource` is the ONLY row and pagination owner.
 * Every prop another owner would use is typed `never`, so a call site cannot hand the grid two.
 */
export type DataSourceDataGridProps<T extends GridValidRowModel = GridValidRowModel> = {
  mode: 'dataSource';
  dataSource: NonNullable<DataGridPremiumProps<T>['dataSource']>;
  /** Supplies search, filters and the committed selection. Its paged cursor is never driven here. */
  enhancedGridState: BaseGridStateWithHelpers;
  rows?: never;
  pageInfo?: never;
  totalCount?: never;
  paginationResetKey?: never;
  resolvePagedGroupRows?: never;
  resolveAllPagedRows?: never;
  getPagedRowGroupKey?: never;
  pagedGroupSelectionScopeKey?: never;
  enableTreeSelection?: never;
} & Omit<
  DataGridProps<T>,
  | 'mode'
  | 'dataSource'
  | 'enhancedGridState'
  | 'rows'
  | 'pageInfo'
  | 'totalCount'
  | 'paginationResetKey'
  | 'resolvePagedGroupRows'
  | 'resolveAllPagedRows'
  | 'getPagedRowGroupKey'
  | 'pagedGroupSelectionScopeKey'
  | 'enableTreeSelection'
>;

type DataGridInnerProps<T extends GridValidRowModel = GridValidRowModel> = DataGridProps<T> & { apiRef: GridApiRef };

export default function DataGrid<T extends GridValidRowModel = GridValidRowModel>(props: DataGridProps<T>) {
  const { mode, enhancedGridState, rows } = props;
  const modedState = enhancedGridState && 'mode' in enhancedGridState ? enhancedGridState : undefined;

  if (mode === 'dataSource') {
    const {
      mode: _mode,
      dataSource,
      enhancedGridState: _state,
      rows: competingRows,
      pageInfo,
      totalCount,
      paginationResetKey,
      resolvePagedGroupRows,
      resolveAllPagedRows,
      getPagedRowGroupKey,
      pagedGroupSelectionScopeKey,
      enableTreeSelection,
      ...dataSourceProps
    } = props;
    if (!dataSource) throw new Error('DataGrid mode="dataSource" requires a `dataSource`.');
    if (!enhancedGridState) throw new Error('DataGrid mode="dataSource" requires an enhancedGridState.');
    const competingOwner = [
      competingRows,
      pageInfo,
      totalCount,
      paginationResetKey ?? undefined,
      resolvePagedGroupRows,
      resolveAllPagedRows,
      getPagedRowGroupKey,
      pagedGroupSelectionScopeKey,
      enableTreeSelection || undefined,
    ].some((value) => value !== undefined);
    if (competingOwner) throw new Error(DATA_SOURCE_OWNER_CONFLICT);

    return (
      <DataSourceDataGrid
        {...dataSourceProps}
        mode="dataSource"
        dataSource={dataSource}
        enhancedGridState={enhancedGridState}
      />
    );
  }

  if (props.dataSource) throw new Error(DATA_SOURCE_OWNER_CONFLICT);

  if (mode === 'paged') {
    if (modedState?.mode !== 'paged') throw new Error(MODE_STATE_MISMATCH('paged'));
    return <PagedDataGrid {...props} mode="paged" enhancedGridState={modedState} />;
  }

  if (mode === 'local') {
    if (modedState?.mode !== 'local') throw new Error(MODE_STATE_MISMATCH('local'));
    if (!rows) throw new Error('DataGrid mode="local" requires `rows`; it filters them in the browser.');
    return <LocalDataGrid {...props} mode="local" enhancedGridState={modedState} rows={rows} />;
  }

  const { apiRef } = props;

  if (!apiRef) {
    return <DataGridRefWrapper {...props} />;
  }

  return <DataGridInner {...props} apiRef={apiRef} />;
}

function DataGridRefWrapper<T extends GridValidRowModel>(props: DataGridProps<T>) {
  const apiRef = useGridApiRef();
  return <DataGridInner {...props} apiRef={apiRef} />;
}

function DataGridInner<T extends GridValidRowModel>({
  columns,
  rows,
  gridKey,
  storageKey,
  apiRef,
  appendToColumns,
  initialState,
  checkboxSelection,
  rowSelectionModel,
  toolbar,
  pinnedRows,
  columnVisibilityModel,
  disabledColumns,
  hiddenByDefault,
  columnOrderToSync,
  onPaginationModelChange,
  onSortModelChange,
  onFilterModelChange,
  onAfterFirstLoad,
  height,
  noBorder,
  surface,
  paginationResetKey,
  rowsReady,
  ...props
}: DataGridInnerProps<T>) {
  const [shouldSyncColumnOrder, setShouldSyncColumnOrder] = useState(false);

  /** DataGrid's column order is only controllable via the ordered column array, so computed columns fire once with the new order; every later call uses the current ordered-column state so user reordering survives. */
  useEffect(() => {
    if (columnOrderToSync) {
      setShouldSyncColumnOrder(true);
    }
  }, [columnOrderToSync]);

  const {
    persistGridState,
    initialState: mergedState,
    columnVisibilityModel: mergedVisibilityModel,
    onColumnVisibilityModelChange,
    forcedColumns,
  } = useGridColumnState({
    storageKey,
    columns,
    apiRef,
    initialState,
    columnVisibilityModel,
    disabledColumns,
    hiddenByDefault,
    toolbar,
    toolbarSlot: props.slots?.toolbar,
    serverFilters: props.enhancedGridState?.gridFilters,
    quickFilterLabel: props.enhancedGridState?.quickFilterOption?.label,
  });

  const computedColumns = useMemo<Array<GridColDef<T>>>(() => {
    const columnState = apiRef.current?.state?.columns;

    let syncColumnOrder = shouldSyncColumnOrder ? columnOrderToSync : null;
    const defaultOrder = Object.keys(columns);

    if (shouldSyncColumnOrder && syncColumnOrder) {
      // synchronize new columns into the synced column order
      const currentSyncColumnOrder = syncColumnOrder;
      const newColumns = defaultOrder.filter((column) => !currentSyncColumnOrder.includes(column));

      if (newColumns.length) {
        syncColumnOrder = [...syncColumnOrder, ...newColumns];
      }

      /** Wait a render before setting the column order, or it resolves to the order already in state. */
      setTimeout(() => {
        setShouldSyncColumnOrder(false);
      });
    }

    const baseOrder = syncColumnOrder || columnState?.orderedFields || defaultOrder;
    const columnOrder = resolveColumnOrder(baseOrder, defaultOrder);

    const columnLookup = columnState?.lookup;

    return columnOrder
      .filter((field) => field in columns)
      .map((field) => {
        const currentColumn = columns[field];
        const storedColumn = (columnLookup && columnLookup[field]) ?? {
          width: currentColumn.width,
          flex: currentColumn.flex,
          computedWidth: undefined,
          hasBeenResized: false,
        };

        // data from stored columns to override
        const { width = 100, computedWidth, flex, hasBeenResized } = storedColumn;

        const column = {
          field,
          ...columns[field], // current column (including renderers, value getters, etc)
          ...appendToColumns?.[field], // extra data
          computedWidth,
          width,
          flex,
          hasBeenResized,
          // A forced column is re-applied over the user's model every render: a switch for it only snaps back.
          hideable: !(field in forcedColumns),
        };

        return withDateValueGetter<T>(column);
      });
  }, [columns, appendToColumns, forcedColumns, apiRef, shouldSyncColumnOrder, columnOrderToSync]);

  const firstLoadState = useRef({
    onAfterFirstLoad,
    filterModel: mergedState.filter?.filterModel ?? { items: [] },
    sortModel: mergedState.sorting?.sortModel ?? [],
  });

  useEffect(() => {
    const firstLoad = firstLoadState.current;
    firstLoad.onAfterFirstLoad?.(firstLoad.filterModel, firstLoad.sortModel);
  }, []);

  const serverFilterPersistenceKey = JSON.stringify({
    gridFilters: props.enhancedGridState?.gridFilters,
    quickFilterLabel: props.enhancedGridState?.quickFilterOption?.label,
  });
  useEffect(() => {
    void serverFilterPersistenceKey;
    persistGridState();
  }, [persistGridState, serverFilterPersistenceKey]);

  // Stable identities for everything handed to MUI below; see `useLatestCallback`.
  const persistOnChange = useLatestCallback(() => persistGridState());
  const handleSortModelChange = useLatestCallback<NonNullable<DataGridPremiumProps['onSortModelChange']>>(
    (...event) => {
      persistGridState();
      onSortModelChange?.(...event);
    }
  );
  const handleFilterModelChange = useLatestCallback<NonNullable<DataGridPremiumProps['onFilterModelChange']>>(
    (model, details) => {
      persistGridState();
      onFilterModelChange?.(model, details);
    }
  );
  const handlePaginationModelChange = useLatestCallback<NonNullable<DataGridPremiumProps['onPaginationModelChange']>>(
    (...event) => {
      persistGridState();
      onPaginationModelChange?.(...event);
    }
  );
  const handleColumnVisibilityModelChange = useLatestCallback(onColumnVisibilityModelChange);
  const loading = props.loading;
  const hostSlotProps = props.slotProps;
  const slotProps = useMemo(
    () => ({ ...hostSlotProps, main: { ...hostSlotProps?.main, 'aria-busy': !!loading } }),
    [hostSlotProps, loading]
  );

  const { focusGrid, blurGrid } = useGridFocus();

  const handleFocusIn = useCallback(
    (event: ReactFocusEvent<HTMLDivElement> | FocusEvent) => {
      const mainGrid = event.target instanceof Element ? event.target.closest('.MuiDataGrid-main') : null;

      if (mainGrid) {
        focusGrid(gridKey);
      }
    },
    [focusGrid, gridKey]
  );

  const handleFocusOut = useCallback(
    (event: ReactFocusEvent<HTMLDivElement> | FocusEvent) => {
      const mainGrid = event.target instanceof Element ? event.target.closest('.MuiDataGrid-main') : null;

      if (mainGrid) {
        blurGrid(gridKey);
      }
    },
    [blurGrid, gridKey]
  );

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const curRef = ref.current;

    curRef?.addEventListener('focusin', handleFocusIn);
    curRef?.addEventListener('focusout', handleFocusOut);
    return () => {
      curRef?.removeEventListener('focusin', handleFocusIn);
      curRef?.removeEventListener('focusout', handleFocusOut);
    };
  }, [handleFocusIn, handleFocusOut]);

  const rowsReadinessScope = useMemo(
    () =>
      JSON.stringify({
        gridFilters: props.enhancedGridState?.gridFilters,
        searchText: props.enhancedGridState?.searchText,
        quickFilterOption: props.enhancedGridState?.quickFilterOption,
        paginationResetKey,
      }),
    [
      props.enhancedGridState?.gridFilters,
      props.enhancedGridState?.searchText,
      props.enhancedGridState?.quickFilterOption,
      paginationResetKey,
    ]
  );

  const resultAnnouncement = useGridResultAnnouncement({
    enhancedGridState: props.enhancedGridState,
    columns,
    rows,
    rowCount: props.rowCount,
    loading: props.loading,
  });

  return (
    <GridCoreProvider>
      {/* MUI renders the toolbar slot here, so it shares this grid's successful-row signal. */}
      <GridRowsReadinessProvider loaded={rowsReady ?? props.loading !== true} scopeKey={rowsReadinessScope}>
        <Box ref={ref} sx={{ width: '100%', ...gridSurfaceSx(resolveGridSurface({ surface, noBorder })) }}>
          <PersistentDataGrid
            columns={computedColumns}
            apiRef={apiRef}
            rows={rows}
            checkboxSelection={checkboxSelection}
            noBorder={noBorder}
            {...props}
            // `slotProps.main` is MUI's hook for the `role="grid"` element, and MUI
            // emits no `aria-busy` of its own. Merged, so paged `aria-rowcount` survives.
            slotProps={slotProps}
            onColumnOrderChange={persistOnChange}
            onColumnWidthChange={persistOnChange}
            onRowGroupingModelChange={persistOnChange}
            rowSelectionModel={rowSelectionModel}
            onSortModelChange={handleSortModelChange}
            onFilterModelChange={handleFilterModelChange}
            onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
            onPaginationModelChange={handlePaginationModelChange}
            onPinnedColumnsChange={persistOnChange}
            disabledColumns={forcedColumns}
            columnVisibilityModel={mergedVisibilityModel}
            initialState={mergedState}
            pinnedRows={pinnedRows}
            toolbar={toolbar}
            height={height}
            gridKey={gridKey}
            storageKey={storageKey}
          />
          <GridLiveRegion announcement={resultAnnouncement} data-testid="grid-result-announcement" />
        </Box>
      </GridRowsReadinessProvider>
    </GridCoreProvider>
  );
}

const DEFAULT_PAGE_SIZE_OPTIONS: NonNullable<DataGridPremiumProps['pageSizeOptions']> = [5, 10, 25, 50, 100];
const DEFAULT_PAGED_PAGE_SIZE = 25;

const isPageSizeOption = (
  pageSize: number,
  options: NonNullable<DataGridPremiumProps['pageSizeOptions']> = DEFAULT_PAGE_SIZE_OPTIONS
) => options.some((option) => (typeof option === 'number' ? option : option.value) === pageSize);

const resolvePagedPageSize = (
  pageSize: number,
  options: NonNullable<DataGridPremiumProps['pageSizeOptions']> = DEFAULT_PAGE_SIZE_OPTIONS
): number => {
  if (isPageSizeOption(pageSize, options)) return pageSize;
  if (isPageSizeOption(DEFAULT_PAGED_PAGE_SIZE, options) || options.length === 0) return DEFAULT_PAGED_PAGE_SIZE;
  const [first] = options;
  return typeof first === 'number' ? first : first.value;
};

/** A data source pages from the initial size as given; one off the options hides the rows-per-page picker. */
const withSelectablePageSize = (
  initialState: DataGridPremiumProps['initialState'],
  options: NonNullable<DataGridPremiumProps['pageSizeOptions']>
): DataGridPremiumProps['initialState'] => {
  const paginationModel = initialState?.pagination?.paginationModel;
  if (paginationModel?.pageSize === undefined || isPageSizeOption(paginationModel.pageSize, options)) {
    return initialState;
  }
  return {
    ...initialState,
    pagination: {
      ...initialState?.pagination,
      paginationModel: { ...paginationModel, pageSize: resolvePagedPageSize(paginationModel.pageSize, options) },
    },
  };
};

/*
 * DataGrid wrapper to optimize column re-ordering
 */
const defaultGetRowId = (row: GridValidRowModel) => row.id;
const DEFAULT_ROW_SELECTION_PROPAGATION = { parents: false, descendants: false };

/**
 * A handler whose identity never changes and that always runs the latest render's closure.
 *
 *   host render ─► new handler ─► DataGridPremium memo misses ─► new root-props context
 *                                                                 └─► EVERY row + cell re-renders
 *
 * MUI publishes its props through a context that each `GridRow` and `GridCell` reads
 * (`useGridRootProps`), so one fresh callback re-rendered the whole grid on every host render.
 * The ref is written during render, so a call MUI makes before mount still reaches the handler.
 */
function useLatestCallback<F extends (...args: never[]) => unknown>(handler: F): F {
  const latest = useRef(handler);
  latest.current = handler;

  return useCallback(((...args: Parameters<F>) => latest.current(...args)) as F, []);
}

export function PersistentDataGrid<T extends GridValidRowModel = GridValidRowModel>({
  toolbar,
  slots,
  columns,
  checkboxSelection,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  slotProps,
  disabledColumns,
  hideFooter,
  apiRef,
  sx,
  initialState,
  defaultGroupingExpansionDepth,
  getRowId = defaultGetRowId,
  shouldPreventRowProcess,
  processRowUpdate,
  onProcessRowUpdateError,
  autoHeight = true,
  variant = 'grid',
  height,
  getRowHeight,
  gridId,
  gridKey,
  storageKey,
  noBorder,
  // Swallowed, not used: the outer wrapper is `DataGridInner`'s. Left on
  // MUI's props it would leak to the DOM as an unknown attribute.
  surface: _surface,
  ...props
}: PersistentDataGridProps<T>) {
  const setHoveredRowId = useGridCore((store) => store.setHoveredRowId);
  const setHoveredField = useGridCore((store) => store.setHoveredField);

  useRowEditSubscriptions(gridKey, apiRef);

  const CustomToolbar = useCallback<MuiToolbarSlot>(
    (slotProps: ComponentProps<MuiToolbarSlot>) => {
      const toolbarSlot = slots?.toolbar;
      if (typeof toolbarSlot === 'function') {
        return createElement(toolbarSlot, slotProps);
      }
      if (toolbarSlot) return toolbarSlot;

      return toolbar ? <Toolbar {...toolbar} /> : null;
    },
    [toolbar, slots?.toolbar]
  );

  const { isGroupExpandedByDefault } = useGridGrouping({ defaultGroupingExpansionDepth });
  const resolvedInitialState = useMemo(
    () => (props.dataSource ? withSelectablePageSize(initialState, pageSizeOptions) : initialState),
    [initialState, pageSizeOptions, props.dataSource]
  );

  const styles = useMemo(
    () => mergeGridSx(baseGridStyles({ variant, noBorder }), { height }, sx),
    [sx, variant, noBorder, height]
  );

  useEffect(() => {
    const api = apiRef.current;
    if (!api) return undefined;

    const unsubscribeCellMouseOver = api.subscribeEvent('cellMouseOver', (event) => setHoveredField(event.field));
    const unsubscribeRowMouseOver = api.subscribeEvent('rowMouseOver', (event) => setHoveredRowId(event.id));
    const unsubscribeRowMouseLeave = api.subscribeEvent('rowMouseLeave', () => {
      setHoveredRowId(null);
      setHoveredField(null);
    });

    return () => {
      unsubscribeCellMouseOver();
      unsubscribeRowMouseOver();
      unsubscribeRowMouseLeave();
    };
  }, [apiRef, setHoveredRowId, setHoveredField]);

  const showMoreRowId = useGridCore((store) => store.showMoreRowId);

  /**
   * Memoised like MUI's own default `GridCell` (fastMemo), which this slot replaces. `GridRow`
   * re-renders on every `updateRows`, and an unmemoised slot took every cell of every row with
   * it; a cell's own grid and store subscriptions still re-render it when its state changes.
   */
  const cellSlot = useMemo(() => {
    const CellComponent = slots?.cell ?? GridCell;
    const Cell = memo((props: GridCellProps) => (
      <CellComponent {...props} data-grid-id={gridId} data-row={props.rowId} />
    ));
    Cell.displayName = 'PersistentDataGridCell';

    return Cell;
  }, [slots?.cell, gridId]);

  // Determine if clipboard paste should be disabled based on canEdit prop
  // MUI's clipboard paste bypasses isCellEditable, so we need to disable it when editing is not allowed
  const disableClipboardPaste = props.disableClipboardPaste ?? props.canEdit === false;
  const showToolbar = props.showToolbar ?? Boolean(toolbar || slots?.toolbar);

  // Everything below is memoised so `DataGridPremium` re-renders only when an input really
  // changed; see `useLatestCallback`.
  const resolveRowHeight = useCallback<NonNullable<DataGridPremiumProps['getRowHeight']>>(
    (params) => {
      if (params.id === showMoreRowId) {
        return 'auto';
      }

      return getRowHeight?.(params) ?? gridRowHeight(params);
    },
    [showMoreRowId, getRowHeight]
  );

  const muiSlotProps = useMemo<DataGridPremiumProps['slotProps']>(
    () => ({
      ...slotProps,
      root: { ...slotProps?.root, 'data-grid-id': gridId },
      row: slotProps?.row,
      columnsManagement: {
        getTogglableColumns: (columns) => {
          return columns
            .filter((column) => {
              // PRESENCE, not truthiness: a `false` entry still forces the
              // column visible on every render, so offering it as a toggle
              // only ever produced a checkbox that snapped straight back.
              return !(column.field in (disabledColumns ?? {}));
            })
            .map((column) => column.field);
        },
      },
    }),
    [slotProps, gridId, disabledColumns]
  );

  const muiSlots = useMemo(
    () => ({ ...slots, cell: cellSlot, toolbar: CustomToolbar }),
    [slots, cellSlot, CustomToolbar]
  );

  const guardedProcessRowUpdate = useCallback<NonNullable<DataGridPremiumProps<T>['processRowUpdate']>>(
    async (newRow, oldRow, params) => {
      const preventProcess = await shouldPreventRowProcess?.(getRowId(oldRow), oldRow, newRow);

      if (preventProcess) {
        throw new Error('Row update prevented', { cause: 'preventRowUpdate' });
      }

      return processRowUpdate ? processRowUpdate(newRow, oldRow, params) : newRow;
    },
    [shouldPreventRowProcess, getRowId, processRowUpdate]
  );

  const handleProcessRowUpdateError = useCallback(
    (error: unknown) => {
      if (error instanceof Error && error.cause === 'preventRowUpdate') {
        return;
      }

      onProcessRowUpdateError?.(error);
    },
    [onProcessRowUpdateError]
  );

  return (
    <GridPerformanceProfiler gridId={gridId ?? storageKey ?? 'unscoped'} storageKey={storageKey}>
      <DataGridPremium
        {...props}
        disableClipboardPaste={disableClipboardPaste}
        // MUI sizes the header row from this prop, never from CSS.
        columnHeaderHeight={props.columnHeaderHeight ?? GRID_COLUMN_HEADER_HEIGHT}
        disableRowSelectionExcludeModel={props.disableRowSelectionExcludeModel ?? true}
        historyStackSize={0}
        rowSelectionPropagation={props.rowSelectionPropagation ?? DEFAULT_ROW_SELECTION_PROPAGATION}
        showToolbar={showToolbar}
        getRowId={getRowId}
        columns={columns}
        disableColumnMenu
        disableColumnFilter
        checkboxSelection={checkboxSelection}
        apiRef={apiRef}
        initialState={resolvedInitialState}
        defaultGroupingExpansionDepth={defaultGroupingExpansionDepth}
        // A data-source host supplies its own STABLE callback: a changing one makes MUI regroup.
        isGroupExpandedByDefault={props.dataSource ? props.isGroupExpandedByDefault : isGroupExpandedByDefault}
        getRowHeight={resolveRowHeight}
        slotProps={muiSlotProps}
        hideFooter={hideFooter}
        slots={muiSlots}
        autoHeight={autoHeight}
        sx={styles}
        processRowUpdate={guardedProcessRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        onCellEditStart={useCallback((_params, event) => {
          if (event.currentTarget instanceof HTMLInputElement || event.currentTarget instanceof HTMLTextAreaElement) {
            event.currentTarget.select();
          }
        }, [])}
        pageSizeOptions={pageSizeOptions}
        disableRowSelectionOnClick={props.disableRowSelectionOnClick ?? true}
      />
    </GridPerformanceProfiler>
  );
}

/**
 * mode="paged" — cursor pagination, server sort/filter.
 *
 *   pageEndCursors[page] ──▶ after   the window the query asks for
 *   filters/sort change  ──▶ page 0  cursors are only valid in order
 *
 * Body moved unchanged from the deleted paged grid. `mode` is destructured off so
 * the inner `<DataGrid>` renders the plain grid instead of recursing back here.
 */
const withoutGroupingColumnSort = (sortModel: GridSortModel): GridSortModel =>
  sortModel.some((item) => isGroupingColumn(item.field))
    ? sortModel.filter((item) => !isGroupingColumn(item.field))
    : sortModel;

function PagedDataGrid<T extends GridValidRowModel>({
  mode: _mode,
  pageInfo,
  totalCount,
  loading,
  enhancedGridState,
  enableSelection = false,
  autoHeight,
  initialState,
  noResultsLabel,
  paginationResetKey,
  resolvePagedGroupRows,
  resolveAllPagedRows,
  getPagedRowGroupKey,
  pagedGroupSelectionScopeKey,
  noBorder = true,
  ...dataGridProps
}: PagedDataGridProps<T>) {
  const init = useRef(false);
  const pageEndCursors = useRef<Record<number, string>>({});
  // Only csv/excel put the grid into export presentation mode; print is driven by MUI.
  const isExporting = useGridExportState((s) => isRowSerializingExport(s.exportFormat));
  const { columns, onPaginationModelChange } = dataGridProps;

  // Row count is only fetched on the first page for performance reasons so we store it here
  const [rowCount, setRowCount] = useState<number>();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: resolvePagedPageSize(enhancedGridState.first, dataGridProps.pageSizeOptions),
  });
  const [sortModel, setSortModel] = useState<GridSortModel>(
    withoutGroupingColumnSort(initialState?.sorting?.sortModel ?? [])
  );

  const printing = useRef(false);
  const handlePaginationModelChange = useCallback(
    (
      model: GridPaginationModel,
      details: Parameters<NonNullable<DataGridPremiumProps['onPaginationModelChange']>>[1]
    ) => {
      // MUI print export writes `{ page: 0, pageSize: <printed rows> }` with no reason, then restores the page it replaced.
      if (details.reason === undefined) {
        printing.current = true;
        return;
      }
      if (printing.current && details.reason === 'stateRestorePreProcessing') {
        printing.current = false;
        return;
      }
      if (!isPageSizeOption(model.pageSize, dataGridProps.pageSizeOptions)) return;
      if (model.page === 0 || pageEndCursors.current[model.page - 1]) {
        if (model.pageSize !== paginationModel.pageSize) {
          model.page = 0;
          pageEndCursors.current = {};
        }
        onPaginationModelChange?.(model, details);
        setPaginationModel(model);
      }
    },
    [onPaginationModelChange, paginationModel.pageSize, dataGridProps.pageSizeOptions]
  );

  const paginationScopeKey = JSON.stringify({
    gridFilters: enhancedGridState.gridFilters,
    sortModel,
    searchText: enhancedGridState.searchText,
    quickFilterOption: enhancedGridState.quickFilterOption,
    paginationResetKey,
  });
  const paginationScopeKeyRef = useRef(paginationScopeKey);

  // A cursor is filed only from a response to the latest request: same window, sort, filters and search.
  const latestRequest = useRef<{ page: string; scope: string } | undefined>(undefined);
  const rowScope = JSON.stringify({
    gridFilters: enhancedGridState.gridFilters,
    quickFilters: enhancedGridState.quickFilterOption?.filters,
    searchText: enhancedGridState.searchText,
  });
  const rowScopeRef = useRef(rowScope);
  rowScopeRef.current = rowScope;
  const requestPage = useCallback(
    (sortItems: GridSortModel, first: number, after: string | undefined) => {
      latestRequest.current = { page: pagedRequestKey({ after, first, sortItems }), scope: rowScopeRef.current };
      enhancedGridState.gridUpdate(sortItems, first, after);
    },
    [enhancedGridState]
  );

  // Callback to fetch more data after pagination change
  useEffect(() => {
    if (paginationScopeKeyRef.current !== paginationScopeKey) {
      return;
    }

    const { pageSize, page } = paginationModel;
    const serverSortModel = withoutGroupingColumnSort(sortModel);

    requestPage(serverSortModel, pageSize, page > 0 ? pageEndCursors.current[page - 1] : undefined);
  }, [paginationModel, paginationScopeKey, requestPage, sortModel]);

  // A cursor belongs to one row-query scope. Reset once when that scope changes.
  useEffect(() => {
    if (paginationScopeKeyRef.current === paginationScopeKey) {
      return;
    }

    paginationScopeKeyRef.current = paginationScopeKey;
    pageEndCursors.current = {};
    latestRequest.current = undefined;
    setRowCount(undefined);

    if (paginationModel.page === 0) {
      requestPage(withoutGroupingColumnSort(sortModel), paginationModel.pageSize, undefined);
      return;
    }

    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, [paginationModel.page, paginationModel.pageSize, paginationScopeKey, requestPage, sortModel]);

  useEffect(() => {
    if (loading || pageInfo === undefined) return;
    const answers = pageInfoRequestKey(pageInfo);
    const request = latestRequest.current;
    if (answers !== undefined && (answers !== request?.page || rowScope !== request.scope)) return;
    if (typeof pageInfo.endCursor === 'string') {
      pageEndCursors.current[paginationModel.page] = pageInfo.endCursor;
    }
  }, [loading, pageInfo, paginationModel.page, rowScope]);

  useEffect(() => {
    init.current = true;
  }, []);

  useEffect(() => {
    if (init.current && !isExporting) {
      enhancedGridState.forceRefetch();
    }
  }, [isExporting, enhancedGridState]);

  // Cache total count for when it's undefined on later pages
  useEffect(() => {
    if (typeof totalCount === 'number') {
      setRowCount(totalCount);
    }
  }, [totalCount]);

  const NoRows = useCallback(
    () => <NoRowsOverlay enhancedGridState={enhancedGridState} columns={columns} noResultsLabel={noResultsLabel} />,
    [enhancedGridState, columns, noResultsLabel]
  );

  const expectedRowsInPage = !loading ? (dataGridProps.rows?.length ?? 0) : enhancedGridState.first;
  const hasNoRows = !dataGridProps.rows?.length;

  /**
   * MUI derives `aria-rowcount` from the rows in state, not from `rowCount`, so a screen reader
   * is told a paged table is one page long. `slotProps.main` is spread AFTER MUI's own aria
   * attributes, so this wins.
   */
  const resolvedTotalCount = typeof totalCount === 'number' ? totalCount : rowCount;
  const ariaRowCount = pagedAriaRowCount({
    totalRowCount: resolvedTotalCount,
    pinnedRows: dataGridProps.pinnedRows,
    columnGroupingModel: dataGridProps.columnGroupingModel,
  });

  /** MUI restarts `aria-rowindex` at 1 on every server page, so it must be offset into the same coordinate space as the total; the offset travels with the count because pinned BOTTOM rows are placed by counting back from it. */
  const rowIndexFrame = useMemo(
    () => ({ offset: isExporting ? 0 : paginationModel.page * paginationModel.pageSize, ariaRowCount }),
    [isExporting, paginationModel.page, paginationModel.pageSize, ariaRowCount]
  );

  const selection = useGridSelection({
    mode: 'paged',
    enableSelection,
    enhancedGridState,
    rows: dataGridProps.rows ?? [],
    apiRef: dataGridProps.apiRef,
    getRowId: dataGridProps.getRowId,
    resolveGroupRows: resolvePagedGroupRows,
    resolveAllRows: resolveAllPagedRows,
    getRowGroupKey: getPagedRowGroupKey,
    selectionScopeKey: pagedGroupSelectionScopeKey,
  });

  return (
    <PagedRowIndexFrameProvider value={rowIndexFrame}>
      <DataGrid
        loading={isExporting || loading}
        paginationResetKey={paginationResetKey}
        rowsReady={!loading && pageInfo !== undefined}
        rowCount={typeof totalCount === 'number' ? totalCount : (rowCount ?? -1)}
        {...dataGridProps}
        noBorder={noBorder}
        autoHeight={autoHeight}
        sx={mergeGridSx(dataGridProps.sx, {
          // An own `undefined` would replace a caller's minHeight; layers merge by top-level key.
          ...(hasNoRows
            ? { minHeight: 320, '--DataGrid-overlayHeight': '320px' }
            : autoHeight
              ? { minHeight: `${expectedRowsInPage * MIN_ROW_HEIGHT}px` }
              : {}),
        })}
        slots={{ ...dataGridProps.slots, noRowsOverlay: NoRows, row: PagedGridRow }}
        slotProps={{
          ...dataGridProps.slotProps,
          loadingOverlay: { variant: 'skeleton' },
          main: { ...dataGridProps.slotProps?.main, 'aria-rowcount': ariaRowCount },
        }}
        initialState={initialState}
        onAfterFirstLoad={(_filterState, sortState) => {
          setSortModel(withoutGroupingColumnSort(sortState));
        }}
        // Pagination
        pagination
        paginationMode={isExporting ? 'client' : 'server'}
        paginationMeta={{ hasNextPage: pageInfo?.hasNextPage }}
        paginationModel={isExporting ? { page: 0, pageSize: 5 } : paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        // Filtering
        filterMode={dataGridProps.filterMode ?? 'server'}
        // Sorting
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={(model) => {
          const next = withoutGroupingColumnSort(model);
          setSortModel((current) => (JSON.stringify(current) === JSON.stringify(next) ? current : next));
        }}
        enhancedGridState={enhancedGridState}
        // Selection
        checkboxSelection={selection.checkboxSelection}
        disableRowSelectionExcludeModel={
          dataGridProps.disableRowSelectionExcludeModel ?? selection.disableRowSelectionExcludeModel
        }
        rowSelectionPropagation={dataGridProps.rowSelectionPropagation ?? selection.rowSelectionPropagation}
        keepNonExistentRowsSelected={selection.keepNonExistentRowsSelected}
        rowSelectionModel={selection.rowSelectionModel}
        onRowSelectionModelChange={selection.onRowSelectionModelChange}
      />
    </PagedRowIndexFrameProvider>
  );
}

/**
 * mode="local" — MUI owns search, sorting, selection and pagination; only the quick-filter
 * buttons and toolbar filters are applied here, in the browser, over EVERY row.
 *
 * Body moved unchanged from the deleted local grid.
 */
function DataSourceDataGrid<T extends GridValidRowModel>({
  mode: _mode,
  enhancedGridState,
  enableSelection = false,
  isParentRow,
  resolveChildRows,
  resolveAllRows,
  getParentRowId,
  selectionScopeKey,
  noBorder = true,
  ...dataGridProps
}: DataSourceDataGridProps<T>) {
  const isExporting = useGridExportState((s) => s.isExporting);
  // Only csv/excel hide the grid behind the skeleton; print clones the live DOM.
  const isSerializingExport = useGridExportState((s) => isRowSerializingExport(s.exportFormat));
  const { apiRef } = dataGridProps;

  // A full-scope export swaps every row in with `setRows`, which a server-side tree roots FLAT.
  // Restoring the snapshot cannot rebuild the tree, so the data source refetches it instead.
  const wasExporting = useRef(false);
  useEffect(() => {
    if (wasExporting.current && !isExporting) void apiRef?.current?.dataSource.fetchRows();
    wasExporting.current = isExporting;
  }, [apiRef, isExporting]);

  // MUI REBUILDS a server tree on every regroup, re-creating each parent with `childrenExpanded: false`
  // and keeping it open only if `isGroupExpandedByDefault` says so. This answers from what is open NOW,
  // through a STABLE identity — a changing callback would itself trigger the regroup that loses it.
  const expandedIds = useRef(new Set<GridRowId>());
  // A new data source is a new tree: what was open in the old one must not open in this one.
  const expandedFor = useRef(dataGridProps.dataSource);
  if (expandedFor.current !== dataGridProps.dataSource) {
    expandedFor.current = dataGridProps.dataSource;
    expandedIds.current = new Set();
  }
  const isGroupExpandedByDefault = useCallback((node: GridGroupNode) => expandedIds.current.has(node.id), []);

  // MUI 9.13 fetches a server parent's children only from its OWN grouping-cell click. Any other
  // expansion — Expand All, a custom toggle, restoring a remembered one — would open an empty group.
  useEffect(() => {
    const api = apiRef?.current;
    if (!api) return;
    return api.subscribeEvent('rowExpansionChange', (node) => {
      if (node.type === 'group') {
        if (node.childrenExpanded) expandedIds.current.add(node.id);
        else expandedIds.current.delete(node.id);
      }
      // MUI leaves a server parent's `children` UNDEFINED until its first fetch, whatever the type says.
      const loadedChildren = Array.isArray(node.children) ? node.children.length : 0;
      if (node.type !== 'group' || !node.childrenExpanded || loadedChildren > 0) return;
      void api.dataSource.fetchRows(node.id);
    });
  }, [apiRef]);

  const selection = useDataSourceSelection({
    enableSelection,
    enhancedGridState,
    apiRef,
    getRowId: dataGridProps.getRowId,
    isParentRow,
    resolveChildRows,
    resolveAllRows,
    getParentRowId,
    selectionScopeKey,
  });

  // MUI must be handed ONE stable empty `rows` for the grid's lifetime. With a data source, `setRows`
  // keeps `rowsBeforePartialUpdates` from the INITIAL prop, and a regroup reuses the fetched rows
  // only while `props.rows` is still that same reference — `undefined` crashes, a fresh `[]` wipes them.
  const stableEmptyRows = useRef<T[]>([]).current;

  const innerProps: DataGridProps<T> = {
    ...dataGridProps,
    rows: stableEmptyRows,
    enhancedGridState,
    noBorder,
    loading: isSerializingExport || dataGridProps.loading,
    // The data source keeps its own per-group windows; MUI's cache would serve stale children.
    dataSourceCache: null,
    isGroupExpandedByDefault,
    paginationMode: 'server',
    sortingMode: 'server',
    filterMode: 'server',
    ...selection,
  };

  return apiRef ? <DataGridInner {...innerProps} apiRef={apiRef} /> : <DataGridRefWrapper {...innerProps} />;
}

function LocalDataGrid<T extends GridValidRowModel>({
  mode: _mode,
  rows,
  loading = false,
  enhancedGridState,
  enableSelection = false,
  enableTreeSelection = false,
  isSelectableParent: _isSelectableParent,
  noBorder = true,
  ...dataGridProps
}: LocalDataGridProps<T>) {
  const isExporting = useGridExportState((s) => isRowSerializingExport(s.exportFormat));

  // The filter reads each cell through these same columns, so a computed column filters on the value it SHOWS (its `valueGetter` / `valueFormatter`), not on a row field it lacks.
  const { columns, apiRef } = dataGridProps;
  const filteredData = useMemo(() => {
    if (loading) return [];
    return enhancedGridState.applyFiltersAndSearch(rows, { columns, apiRef });
  }, [
    rows,
    loading,
    columns,
    apiRef,
    enhancedGridState,
    enhancedGridState.searchText,
    enhancedGridState.quickFilterOption,
    enhancedGridState.gridFilters,
  ]);

  const hasNoRows = !loading && filteredData.length === 0;

  const selection = useGridSelection({
    mode: 'local',
    enableSelection,
    enableTreeSelection,
    enhancedGridState,
    rows: filteredData,
    apiRef: dataGridProps.apiRef,
    getRowId: dataGridProps.getRowId,
    getTreeDataPath: dataGridProps.getTreeDataPath,
    rowSelectionModel: dataGridProps.rowSelectionModel,
    onRowSelectionModelChange: dataGridProps.onRowSelectionModelChange,
  });

  return (
    <DataGrid
      loading={isExporting || loading}
      noBorder={noBorder}
      {...dataGridProps}
      rows={filteredData}
      sx={mergeGridSx(dataGridProps.sx, hasNoRows ? { minHeight: '50vh' } : undefined)}
      slotProps={{ ...dataGridProps.slotProps, loadingOverlay: { variant: 'skeleton' } }}
      // Let MUI handle everything client-side
      paginationMode="client"
      filterMode="client"
      sortingMode="client"
      // Native selection functionality
      checkboxSelection={selection.checkboxSelection}
      keepNonExistentRowsSelected={selection.keepNonExistentRowsSelected}
      // Tree selection support
      rowSelectionModel={selection.rowSelectionModel}
      onRowSelectionModelChange={selection.onRowSelectionModelChange}
      disableRowSelectionExcludeModel={
        dataGridProps.disableRowSelectionExcludeModel ?? selection.disableRowSelectionExcludeModel
      }
      rowSelectionPropagation={dataGridProps.rowSelectionPropagation ?? selection.rowSelectionPropagation}
      // Enhanced grid state for toolbar compatibility
      enhancedGridState={enhancedGridState}
    />
  );
}
